import httpClient from '@/httpClient'
import {
  SendVerificationCodeRequestSchema,
  SendVerificationCodeResponseSchema,
  ConfirmVerificationCodeRequestSchema,
  ConfirmVerificationCodeResponseSchema,
  type SendVerificationCodeRequest,
  type SendVerificationCodeResponse,
  type ConfirmVerificationCodeRequest,
  type ConfirmVerificationCodeResponse
} from '@/pages/signup/config/signup.types'
import {
  EnableMfaSmsRequestSchema,
  type EnableMfaSmsRequest
} from './accountSettings.types'
import { handleError, hasInternalErrorCode } from '@/utils/error.utils'
import * as v from '@/types/valibot.types'

const MY_ACCOUNT_URL = '/api/myaccount'

// Coded 400s are expected business outcomes, so only uncoded failures go to Raygun
function reportUncodedError(error: unknown, action: string): void {
  if (!hasInternalErrorCode(error)) handleError({ error, action })
}

export async function sendVerificationCode(
  payload: SendVerificationCodeRequest
): Promise<SendVerificationCodeResponse> {
  const endpoint = `${MY_ACCOUNT_URL}/sendverificationcode`

  try {
    v.parse(SendVerificationCodeRequestSchema, payload)

    const { data } = await httpClient.post<SendVerificationCodeResponse>(
      endpoint,
      payload
    )

    v.parse(SendVerificationCodeResponseSchema, data)

    return data
  } catch (error) {
    reportUncodedError(error, 'sendVerificationCode')

    throw error
  }
}

export async function confirmVerificationCode(
  payload: ConfirmVerificationCodeRequest
): Promise<ConfirmVerificationCodeResponse> {
  const endpoint = `${MY_ACCOUNT_URL}/confirmverificationcode?verifyCode=${payload.verifyCode}`

  try {
    v.parse(ConfirmVerificationCodeRequestSchema, payload)

    const { data } =
      await httpClient.put<ConfirmVerificationCodeResponse>(endpoint)

    v.parse(ConfirmVerificationCodeResponseSchema, data)

    return data
  } catch (error) {
    reportUncodedError(error, 'confirmVerificationCode')

    throw error
  }
}

// Shared by account settings and signup. Responds 200 with an empty body, so
// there is nothing to validate on success.
export async function enableMfaSms(
  payload: EnableMfaSmsRequest
): Promise<void> {
  const endpoint = `${MY_ACCOUNT_URL}/enablemfasms`

  try {
    v.parse(EnableMfaSmsRequestSchema, payload)

    await httpClient.post(endpoint, payload)
  } catch (error) {
    reportUncodedError(error, 'enableMfaSms')

    throw error
  }
}
